import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useAuthStore } from '../stores/authStore';

/** Maps a login/Google auth failure to a localized message. */
function loginErrorMessage(
  err: any,
  t: TFunction,
  fallbackKey = 'login.invalidCredentials',
): string {
  const status = err?.response?.status;
  if (status === 429) return t('login.tooManyAttempts');
  if (status === 403) return t('login.noOrganization');
  return t(fallbackKey);
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              theme?: string;
              size?: string;
              width?: number;
              text?: string;
              shape?: string;
              locale?: string;
            },
          ) => void;
        };
      };
    };
  }
}

export default function Login() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login, loginWithGoogle } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password);
      const { needsTenantSelection, tenant, user } = useAuthStore.getState();
      if (!tenant && user?.superAdmin) navigate('/tenants');
      else navigate(needsTenantSelection || !tenant ? '/select-tenant' : '/');
    } catch (err: any) {
      setError(loginErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  };

  const onGoogleCredential = useCallback(
    async (credential: string) => {
      setError(null);
      setLoading(true);
      try {
        await loginWithGoogle(credential);
        const { needsTenantSelection, tenant, user } = useAuthStore.getState();
        if (!tenant && user?.superAdmin) navigate('/tenants');
        else navigate(needsTenantSelection || !tenant ? '/select-tenant' : '/');
      } catch (err: any) {
        setError(loginErrorMessage(err, t, 'login.googleError'));
      } finally {
        setLoading(false);
      }
    },
    [loginWithGoogle, navigate, t],
  );

  useEffect(() => {
    if (!googleClientId) {
      return;
    }
    const renderButton = () => {
      if (!window.google || !googleButtonRef.current) {
        return;
      }
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: (response) => {
          void onGoogleCredential(response.credential);
        },
      });
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: 'outline',
        size: 'large',
        width: 320,
        text: 'continue_with',
        shape: 'pill',
        locale: i18n.language,
      });
    };

    if (window.google) {
      renderButton();
      return;
    }
    const scriptId = 'google-identity-services';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', renderButton);
    return () => script?.removeEventListener('load', renderButton);
  }, [googleClientId, onGoogleCredential, i18n.language]);

  return (
    <div className="grid min-h-screen bg-paper lg:grid-cols-[1.1fr_1fr]">
      {/* Brand panel - editorial, reassuring */}
      <div className="relative hidden overflow-hidden bg-brand px-12 py-14 text-brand-contrast lg:flex lg:flex-col lg:justify-between">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-lg bg-brand-contrast/15 font-display text-xl font-semibold">
            H
          </span>
          <span className="font-display text-xl font-semibold">{t('common.appName')}</span>
        </div>
        <div className="max-w-md">
          <p className="eyebrow !text-brand-contrast/70">{t('login.subtitle')}</p>
          <p className="mt-4 font-display text-3xl font-medium leading-tight text-brand-contrast">
            {t('login.pitch')}
          </p>
        </div>
        <div className="text-sm text-brand-contrast/60">
          © {new Date().getFullYear()} {t('common.appName')}
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center px-6 py-12">
        <form onSubmit={onSubmit} className="w-full max-w-sm">
          <div className="lg:hidden">
            <span className="font-display text-2xl font-semibold text-brand">
              {t('common.appName')}
            </span>
          </div>
          <h1 className="page-title mt-2">{t('login.signIn')}</h1>
          <p className="mt-1 mb-8 text-sm text-ink-muted">{t('login.subtitle')}</p>

          {error && (
            <div className="mb-5 rounded-lg bg-expense-tint px-3 py-2.5 text-sm font-medium text-expense">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label className="field-label" htmlFor="email">
              {t('login.email')}
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="input"
            />
          </div>

          <div className="mb-6">
            <label className="field-label" htmlFor="password">
              {t('login.password')}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="input"
            />
          </div>

          <button type="submit" disabled={loading} className="btn btn-primary w-full">
            {loading ? t('login.signingIn') : t('login.signIn')}
          </button>

          {googleClientId && (
            <>
              <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-wide text-ink-muted">
                <span className="h-px flex-1 bg-line-strong" />
                {t('login.or')}
                <span className="h-px flex-1 bg-line-strong" />
              </div>
              <div ref={googleButtonRef} className="flex justify-center" />
            </>
          )}
        </form>
      </div>
    </div>
  );
}
